import { DOCUMENT } from "@angular/common";
import { inject, Injectable, NgZone } from "@angular/core";

/** Odstęp sprawdzania stanu serwera (ms). */
export const MAINTENANCE_POLL_INTERVAL_MS = 30000;

/** Lekki plik statyczny – w trakcie przerwy nginx zwraca dla niego 503 z X-Maintenance. */
const PROBE_URL = "/assets/version.json";

/**
 * Wykrywa przerwę techniczną i nowe wdrożenie w już otwartej aplikacji.
 *
 * SPA nie odpytuje serwera przy zmianie widoku, więc bez tego bezczynna karta
 * nie dowiedziałaby się o przerwie. Co MAINTENANCE_POLL_INTERVAL_MS (oraz po
 * powrocie do karty) pobiera version.json:
 *  - 503 + X-Maintenance  → przeładowanie; serwer poda stronę przerwy, która
 *    sama wróci do aplikacji po zakończeniu prac,
 *  - inna wersja niż przy starcie → przeładowanie; stare fragmenty (chunki)
 *    zostały usunięte przy wdrożeniu (rsync --delete), nawigacja by się sypała.
 *
 * Używa fetch zamiast HttpClient – zapytania nie przechodzą przez interceptor
 * błędów (żadnych toastów z cyklicznego sprawdzania).
 */
@Injectable({ providedIn: "root" })
export class MaintenanceWatchService {
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);

  private timer: ReturnType<typeof setInterval> | null = null;
  private knownVersion: string | null = null;
  private reloading = false;

  private readonly onVisibilityChange = (): void => {
    if (this.document.visibilityState === "visible") {
      void this.check();
    }
  };

  /** Uruchamia nasłuch (wywoływane raz, przy starcie aplikacji). */
  start(): void {
    if (this.timer !== null || !this.document.defaultView) {
      return;
    }
    // Poza strefą Angulara – cykliczne sprawdzanie nie wyzwala detekcji zmian.
    this.zone.runOutsideAngular(() => {
      this.timer = setInterval(() => void this.check(), MAINTENANCE_POLL_INTERVAL_MS);
      this.document.addEventListener("visibilitychange", this.onVisibilityChange);
    });
    // Pierwsze sprawdzenie zapamiętuje wersję, z którą wystartowała aplikacja.
    void this.check();
  }

  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.document.removeEventListener("visibilitychange", this.onVisibilityChange);
  }

  /** Przerwa wykryta (np. przez interceptor po 503 z X-Maintenance) – przeładowanie. */
  maintenanceDetected(): void {
    this.reloadOnce();
  }

  async check(): Promise<void> {
    if (this.reloading) {
      return;
    }

    let response: Response;
    try {
      response = await fetch(PROBE_URL, { cache: "no-store", credentials: "same-origin" });
    } catch {
      // Chwilowy brak sieci – spróbujemy w kolejnym cyklu.
      return;
    }

    if (response.status === 503 && response.headers.get("X-Maintenance")) {
      this.reloadOnce();
      return;
    }
    if (!response.ok) {
      return;
    }

    const version = await response
      .json()
      .then((body: { version?: unknown }) => (typeof body?.version === "string" ? body.version : null))
      .catch(() => null);
    if (!version) {
      return;
    }

    if (this.knownVersion === null) {
      this.knownVersion = version;
    } else if (version !== this.knownVersion) {
      this.reloadOnce();
    }
  }

  protected reload(): void {
    this.document.defaultView?.location.reload();
  }

  private reloadOnce(): void {
    if (this.reloading) {
      return;
    }
    this.reloading = true;
    this.stop();
    this.reload();
  }
}
